import { useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  MaudSectionHeader,
  SkeletonLoader,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientCoverage } from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { getSortedCoverageListForPatient } from '~/lib/util/patientCoverage.util';
import { useGetPatientCoveragesQuery } from '~/store/services/clinicalData';

import PatientAddCoverage from './PatientAddCoverage';
import PatientCoverageList from './PatientCoverageList';

const TOP_LEVEL_ID = 'patient-coverages';

interface PatientCoveragesProps {
  hasPatientDashboardPermission: boolean;
  patientId: string;
  patientBasicDetails: Patient;
}

const PatientCoverages = ({
  hasPatientDashboardPermission,
  patientId,
  patientBasicDetails,
}: PatientCoveragesProps) => {
  const [patientCoverages, setPatientCoverages] = useState<PatientCoverage[]>(
    [],
  );
  const [isAddCoverage, setIsAddCoverage] = useState<boolean>(false);

  const { trackEvent } = useAnalytics();

  const {
    isLoading: isGetPatientCoveragesLoading,
    data: getPatientCoveragesResponse,
    error: getPatientCoveragesError,
    isSuccess: isGetPatientCoveragesSuccess,
  } = useGetPatientCoveragesQuery({ patientId });

  useEffect(() => {
    if (isGetPatientCoveragesSuccess) {
      setPatientCoverages(
        getSortedCoverageListForPatient(getPatientCoveragesResponse),
      );
    } else if (getPatientCoveragesError) {
      console.error(
        'Error getting patient coverages',
        getPatientCoveragesError,
      );
    }
  }, [
    getPatientCoveragesError,
    getPatientCoveragesResponse,
    isGetPatientCoveragesSuccess,
  ]);

  const handleOnAdd = () => {
    setIsAddCoverage(false);
    document.getElementById(`${TOP_LEVEL_ID}__section`).scrollIntoView({
      behavior: 'auto',
      block: 'start',
    });
  };

  const handleHeaderClick = () => {
    trackEvent(PatientEvents.addCoverageProfileTabButtonClicked, {
      Source: AnalyticsSourceValues.PATIENT_TAB,
    });
    if (hasPatientDashboardPermission) {
      setIsAddCoverage(true);
    }
  };

  const headerTitle = hasPatientDashboardPermission
    ? 'Add coverage'
    : undefined;

  return (
    <>
      <ChakraFlex
        flexDirection="column"
        id={`${TOP_LEVEL_ID}__section`}
        marginBottom="25px"
        marginTop="50px"
        width="100%"
      >
        <MaudSectionHeader
          count={patientCoverages?.length}
          id={TOP_LEVEL_ID}
          onAdd={handleHeaderClick}
          onAddTitle={headerTitle}
          style="secondary"
          title="Coverage"
        />
      </ChakraFlex>
      {hasPatientDashboardPermission && isAddCoverage && (
        <ChakraBox
          borderColor="neutral.line"
          borderRadius="16px"
          borderStyle="solid"
          borderWidth="1px"
          marginY="5px"
          padding="23.5px 16px"
        >
          <PatientAddCoverage
            onAdd={handleOnAdd}
            onDiscard={() => setIsAddCoverage(false)}
            patientBasicDetails={patientBasicDetails}
            patientCoverages={patientCoverages}
            patientId={patientId}
          />
        </ChakraBox>
      )}

      <SkeletonLoader
        id={TOP_LEVEL_ID}
        isLoaded={!isGetPatientCoveragesLoading}
      >
        {isEmpty(patientCoverages) ? (
          <EmptySectionCard
            caption="There are no coverages associated with this patient"
            id={TOP_LEVEL_ID}
          />
        ) : (
          <PatientCoverageList
            coverages={patientCoverages}
            hasPatientDashboardPermission={hasPatientDashboardPermission}
            onAdd={handleOnAdd}
            patientBasicDetails={patientBasicDetails}
            patientId={patientId}
          />
        )}
      </SkeletonLoader>
    </>
  );
};

export default PatientCoverages;
